import { useState } from "react";
import { useApi, apiPost } from "@/lib/api";
import { useQueryClient } from "@tanstack/react-query";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Bell, Check, CheckCheck } from "lucide-react";

const SEV = { danger: "#DC2626", warning: "#D97706", info: "#2563EB", success: "#16A34A" };
const TABS = [["all", "All"], ["risk", "Risk"], ["budget", "Budget"], ["savings", "Savings"]];

export function NotificationCenter({ profileId, onOpenApp }) {
  const qc = useQueryClient();
  const [tab, setTab] = useState("all");
  const { data } = useApi(["os-notifs", profileId], "/notifications", { profile_id: profileId, filter: "all" });
  const items = data?.items || [];
  const unread = data?.unread || { all: 0, risk: 0, budget: 0, savings: 0 };
  const counts = data?.counts || { all: 0, risk: 0, budget: 0, savings: 0 };
  const shown = tab === "all" ? items : items.filter((n) => n.type === tab);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["os-notifs", profileId] });
  const markRead = async (id) => { await apiPost("/notifications/read", { profile_id: profileId, id }); invalidate(); };
  const markAll = async () => { await apiPost("/notifications/read-all", { profile_id: profileId, filter: tab }); invalidate(); };
  const openItem = async (n) => { if (!n.read) await markRead(n.id); onOpenApp(n.appKey); };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="h-8 w-8 rounded-md hover:bg-nav-chrome/10 flex items-center justify-center relative" data-testid="os-notifications" aria-label="Notifications">
          <Bell className="h-4 w-4" />
          {unread.all > 0 && <span data-testid="notif-unread-badge" className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-sev-danger text-white text-[9px] font-semibold flex items-center justify-center">{unread.all}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[340px] p-0" data-testid="notif-center">
        <div className="flex items-center justify-between px-3 py-2.5 border-b">
          <span className="text-sm font-semibold">Notifications</span>
          <button onClick={markAll} data-testid="notif-mark-all" disabled={unread[tab] === 0}
            className="text-[11px] inline-flex items-center gap-1 text-muted-foreground hover:text-foreground disabled:opacity-40 transition-colors">
            <CheckCheck className="h-3.5 w-3.5" />Mark all read
          </button>
        </div>
        <div className="flex items-center gap-1 px-2 py-2 border-b">
          {TABS.map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`notif-filter-${k}`}
              className={"px-2.5 py-1 rounded-full text-[11px] font-medium inline-flex items-center gap-1 transition-colors " + (tab === k ? "bg-navy text-white" : "text-muted-foreground hover:bg-muted")}>
              {l}{unread[k] > 0 && <span className={"min-w-3.5 h-3.5 px-1 rounded-full text-[9px] flex items-center justify-center " + (tab === k ? "bg-white/25" : "bg-sev-danger/15 text-sev-danger")}>{unread[k]}</span>}
            </button>
          ))}
        </div>
        <ScrollArea className="max-h-80">
          {shown.length ? shown.map((n) => (
            <div key={n.id} data-testid={`notif-${n.type}`} className={"group flex items-start gap-2 px-3 py-2.5 border-b last:border-0 cursor-pointer hover:bg-muted/50 transition-colors " + (n.read ? "opacity-60" : "")}
              onClick={() => openItem(n)}>
              <span className="h-2 w-2 rounded-full mt-1.5 shrink-0" style={{ background: n.read ? "transparent" : (SEV[n.severity] || SEV.info), border: n.read ? "1px solid hsl(var(--border))" : "none" }} />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium leading-tight">{n.title}</p>
                <p className="text-[11px] text-muted-foreground leading-tight mt-0.5 truncate">{n.subtitle}</p>
              </div>
              {!n.read && (
                <button onClick={(e) => { e.stopPropagation(); markRead(n.id); }} data-testid={`notif-read-${n.id}`}
                  className="opacity-0 group-hover:opacity-100 h-6 w-6 rounded-md hover:bg-background flex items-center justify-center shrink-0 transition-opacity" title="Mark as read">
                  <Check className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
              )}
            </div>
          )) : <div className="px-3 py-8 text-sm text-muted-foreground text-center">All clear — nothing here.</div>}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
